import { useRef, useState } from "react";
import { useTranslation } from "hooks/useTranslation";
import DropdownItem from "./components/DropdownItem";
import { ProductsDropdownContent } from "./components/ProductDropdownContent";
import { TooltipLikePopover } from "shared/components/TooltipLikePopover";
import { DynamicLink } from "shared/components/DynamicLink";
import { useDialog } from "hooks/useDialog";
import { useOutsideClick } from "hooks/useOutsideClick";
import { PrimaryDialog } from "shared/components/PrimaryDialog";
import { AboutUs } from "./components/AboutUs";
import { Locations } from "./components/Locations";
import styles from "./Header.module.scss";
import RedLogo from "assets/logos/new-red-logo.png";
import {
  IconStore,
  IconChevronDown,
  IconArrowDownToLine,
  IconInfoTransparent,
  IconLocationTransparent, IconCurrentLocation,
} from "icons";
import { TechnologyDropdownContent } from "./components/TechnologyDropdownContent";
import { logout } from "../../reduxStore/slices/authSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DownloadsDropdownContent } from "./components/DownloadsDropdownContent";
import { SampleProcessing } from "./components/SampleProcessing";

const downloadBuildUrl = `${process.env.PUBLIC_URL}/build.zip`;

const DIALOG_COMPONENTS = {
  aboutUs: props => <AboutUs {...props} />,
  locations: props => <Locations {...props} />,
  sampleProcessing: props => <SampleProcessing {...props} />,
};

export const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isOpenPopover, setIsOpenPopover] = useState(false);
  const [isOpenSectionsPopover, setIsOpenSectionsPopover] = useState(false);

  const sectionDialogRef = useRef(null);

  const sectionDialog = useDialog();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  const isAdmin = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return user && (user.role === 'admin' || user.role === 'super_admin');
  }

  useOutsideClick(
    sectionDialogRef,
    () => sectionDialog.closeDialog(),
    null,
    []
  );

  const technologyLinks = [
    { name: translate("fluxing") },
    { name: translate("preheating") },
    { name: translate("soldering") },
    { name: translate("other") },
  ];
  const marketSectorLinks = [
    { label: translate("Automotive/EV"), name: "Automotive/EV" },
    { label: translate("Aerospace"), name: "Aerospace" },
    { label: translate("Medical"), name: "Medical" },
    { label: translate("Contract Manufacturing"), name: "contract"},
  ];

  const sectionPopUps = [
    {
      name: translate("About Us"),
      icon: <IconInfoTransparent />,
      key: "aboutUs",
    },
    {
      name: translate("Locations"),
      icon: <IconLocationTransparent />,
      key: "locations",
    },
    {
      name: translate("Sample Processing"),
      icon: <IconInfoTransparent />,
      key: "sampleProcessing",
    }
  ];

  const handleOpenPopover = () => setIsOpenPopover(true);

  const handleClosePopover = event => {
    event.stopPropagation();
    setIsOpenPopover(false);
  };

  const handleOpenSectionsPopover = () => setIsOpenSectionsPopover(true);

  const handleCloseSectionsPopover = event => {
    event.stopPropagation();
    setIsOpenSectionsPopover(false);
  };

  const handleClickToSec = (e, key) => {
    handleCloseSectionsPopover(e);
    sectionDialog.openDialog({ key });
  };

  return (
    <>
      <PrimaryDialog
        className={styles.sectionDialogPaper}
        blurBackdrop
        backdropColor={""}
        mode="dark"
        size="lg-wide"
        radius="radius-20"
        open={sectionDialog.isOpen}
      >
        <div
          className={styles.sectionPopupContentContainer}
          ref={sectionDialogRef}
        >
          {!!sectionDialog.dialogData?.key &&
            DIALOG_COMPONENTS[sectionDialog.dialogData.key]({
              hasCloseIcon: true,
              handleClose: sectionDialog.closeDialog,
            })}
        </div>
      </PrimaryDialog>
      <div className={`${styles.header} ${styles.transparent}`}>
        <TooltipLikePopover
          anchorClassName={styles.logoTooltipAnchor}
          open={isOpenSectionsPopover}
          onClose={handleCloseSectionsPopover}
          onOpen={handleOpenSectionsPopover}
          anchorElement={
            <DynamicLink to="/" className={styles.logo}>
              <img src={RedLogo} alt="Pillar House" />
            </DynamicLink>
          }
        >
          {sectionPopUps.map((sp, idx) => (
            <div
              key={idx}
              queryParams={{ name: sp.name }}
              className={styles.popoverItem}
              onClick={e => handleClickToSec(e, sp.key)}
            >
              {sp.icon}
              {sp.name}
            </div>
          ))}
        </TooltipLikePopover>
        <nav className={styles.navigation}>
          <DropdownItem
            number={1}
            isOpen={openDropdown === 1}
            setIsOpen={setOpenDropdown}
            label={translate("technology")}
          >
            <TechnologyDropdownContent closeDropdown={() => setOpenDropdown(null)}/>
          </DropdownItem>
          <DropdownItem
            number={2}
            isOpen={openDropdown === 2}
            setIsOpen={setOpenDropdown}
            label={translate("products")}
          >
            <ProductsDropdownContent
              closeDropdown={() => setOpenDropdown(null)}
            />
          </DropdownItem>
          <DropdownItem
            number={3}
            isOpen={openDropdown === 3}
            setIsOpen={setOpenDropdown}
            label={translate("Technical Downloads")}
          >
            <DownloadsDropdownContent
              closeDropdown={() => setOpenDropdown(null)}
            />
          </DropdownItem>
        </nav>
        <TooltipLikePopover
          contentClassName={styles.marketTooltipContent}
          open={isOpenPopover}
          onClose={handleClosePopover}
          onOpen={handleOpenPopover}
          anchorElement={
            <div className={styles.marketSector}>
              <IconStore color="#fff" /> {translate("market sector")}{" "}
              <IconChevronDown color="#fff" />
            </div>
          }
        >
          {marketSectorLinks.map((msl, idx) => (
            <DynamicLink
              key={idx}
              queryParams={{ name: msl.name }}
              className={styles.popoverItem}
              to="/market"
              onClick={handleClosePopover}
            >
              {msl.label}
            </DynamicLink>
          ))}
        </TooltipLikePopover>
        <DynamicLink
          style={{ color: "#fff", fontSize: '16px' }}
          className={styles.popoverItem}
          onClick={handleLogout}
        >
          Logout
        </DynamicLink>
        {isAdmin() && <a
          href={downloadBuildUrl}
          download="pillarhouse.zip"
          className={styles.downloadItemContainer}
        >
          <IconArrowDownToLine />
        </a>}
      </div>
    </>
  );
};
